/**
 * Play levels, one rule for every local game: Rookie, Pro and Tour (Cole,
 * 2026-10-29, replacing the per-seat KID toggle so a beginner is never
 * singled out). Tour is the normal field and the default. Rookie is the old
 * kid field exactly (Cole, 2026-08-09 for Poker: "kid toggle is basically dad
 * distance / 3. so if dad gets a 15 footer kid gets a 5 footer"; 2026-10-28
 * for Football, Baseball, Line Four, Turf War and Darts). Pro is the same
 * rule at one half.
 *
 * The rule: the adult distance over the level's divisor (3 Rookie, 2 Pro),
 * whole feet, never under 5 ft (`levelDistanceFt`; Poker's rule).
 *
 * The five field games lay their targets out as evenly spaced ladders (cups
 * 9 ft apart, 5-ft columns, 3-ft bands). Applied edge by edge, the rule
 * collapses some of them: the 5-ft floor lands Baseball's bunt and single on
 * the same 5 ft, Turf War's first zone (10-15) becomes 5-5, and so on. So a
 * ladder takes the rule at its first distance and the divided spacing (whole
 * feet, at least 1): it stays evenly spaced, as the adult one is
 * (`levelLadder`). Rings and radii (a tolerance, not a distance) are divided,
 * unrounded (`levelToleranceScale`).
 *
 * Every level's putt is scored by the SAME rules as Tour's: `LevelDistanceMap`
 * is a piecewise-linear map through (0, 0) and every (adult, level) pair of a
 * mode's ladder, so a ball at a Rookie edge reads as the adult ball at the
 * adult edge, and a ball inside a Pro ring reads as the adult ball inside the
 * adult ring. The engines never learn a seat's level.
 *
 * The make model is the adult one on every level (Cole, 2026-10-29). On
 * Rookie Football and Baseball the cups stand 3 ft apart, inside the adult
 * capture window (about 3.6 ft at stimp 10), so a straight putt can drop in a
 * nearer cup it rolls over; Cole accepted that. Pro's cups stand 5 ft apart.
 *
 * Local play only: a live online match never has a level other than Tour,
 * and nothing here is ever sent to the server.
 *
 * Pure: no React Native, so node tests and the TV fixtures share it.
 */
import type { PuttSceneExtraCup } from '../../../../packages/putt-scene/src/cupMarkers';
import type { PuttSceneRolloutBand } from '../../../../packages/putt-scene/src/SceneContent';

export type PlayLevel = 'rookie' | 'pro' | 'tour';
/** The levels that shorten the field. */
export type ShortLevel = Exclude<PlayLevel, 'tour'>;

/** Shortest first, as a setup lists them. */
export const PLAY_LEVELS: readonly PlayLevel[] = ['rookie', 'pro', 'tour'];
export const SHORT_LEVELS: readonly ShortLevel[] = ['rookie', 'pro'];

export const LEVEL_DIVISOR: Readonly<Record<PlayLevel, number>> = { rookie: 3, pro: 2, tour: 1 };
export const LEVEL_MIN_DISTANCE_FT = 5;

/** The setup pill's word and the scoreboard tag. */
export const PLAY_LEVEL_LABEL: Readonly<Record<PlayLevel, string>> = { rookie: 'ROOKIE', pro: 'PRO', tour: 'TOUR' };
/** Sentence case, for notes and accessibility. */
export const PLAY_LEVEL_NAME: Readonly<Record<PlayLevel, string>> = { rookie: 'Rookie', pro: 'Pro', tour: 'Tour' };
/** How far a level plays, for the setup notes. */
export const PLAY_LEVEL_DISTANCE: Readonly<Record<PlayLevel, string>> = {
  rookie: 'a third of the distance',
  pro: 'half the distance',
  tour: 'the full distance',
};

/** The pill's tap: Tour, then Pro, then Rookie, then back to Tour (shorter each tap). */
export function nextPlayLevel(level: PlayLevel): PlayLevel {
  return level === 'tour' ? 'pro' : level === 'pro' ? 'rookie' : 'tour';
}

/**
 * A level read back from anything saved: a level name as is, and an old
 * per-seat KID flag (true) as Rookie, which is what KID played.
 */
export function playLevelFrom(value: unknown): PlayLevel {
  if (value === 'rookie' || value === 'pro' || value === 'tour') return value;
  if (value === true) return 'rookie';
  if (value && typeof value === 'object') {
    const saved = value as { level?: unknown; kid?: unknown };
    if (saved.level !== undefined) return playLevelFrom(saved.level);
    if (saved.kid === true) return 'rookie';
  }
  return 'tour';
}

// Poker's original names (pokerDeck.ts re-exports them): Rookie's rule.
export const KID_DISTANCE_DIVISOR = LEVEL_DIVISOR.rookie;
export const KID_MIN_DISTANCE_FT = LEVEL_MIN_DISTANCE_FT;

/** The level's divisor, whole feet, never under 5 ft: Poker's hole, and the first distance of every ladder. Tour is the identity. */
export function levelDistanceFt(adultFt: number, level: PlayLevel): number {
  if (level === 'tour') return adultFt;
  return Math.max(LEVEL_MIN_DISTANCE_FT, Math.round(adultFt / LEVEL_DIVISOR[level]));
}

/** Rookie's hole: a third, whole feet, never under 5 ft. */
export function kidDistanceFt(adultFt: number): number {
  return levelDistanceFt(adultFt, 'rookie');
}

/** A ladder's spacing or a band's depth on a level: divided, whole feet, at least 1. */
export function levelSpacingFt(adultFt: number, level: PlayLevel): number {
  if (level === 'tour') return adultFt;
  return Math.max(1, Math.round(adultFt / LEVEL_DIVISOR[level]));
}

/** A ring or radius on a level: divided, not rounded (a 3 ft ring is 1 ft on Rookie, 1.5 ft on Pro). */
export function levelToleranceScale(level: PlayLevel): number {
  return 1 / LEVEL_DIVISOR[level];
}

export interface LevelDistanceMap {
  readonly level: ShortLevel;
  /** (adult ft, level ft) pairs, both ascending, (0, 0) first. */
  readonly anchors: ReadonlyArray<readonly [number, number]>;
  /** Level feet per adult foot past the last anchor; omitted, the plain divisor (1/3, 1/2). */
  readonly beyondScale?: number;
}

/**
 * A map through (0, 0) and the given pairs. Throws unless both columns climb.
 * `beyondScale`: the slope past the last pair, for a field that keeps its
 * ladder's spacing out to the end (Football's yards, which the scene paints
 * at one scale).
 */
export function levelDistanceMap(
  level: ShortLevel,
  pairs: ReadonlyArray<readonly [number, number]>,
  options: { beyondScale?: number } = {},
): LevelDistanceMap {
  const anchors: Array<readonly [number, number]> = [[0, 0], ...pairs];
  for (let i = 1; i < anchors.length; i += 1) {
    if (!(anchors[i][0] > anchors[i - 1][0]) || !(anchors[i][1] > anchors[i - 1][1])) {
      throw new RangeError(`${level} distance map is not ascending at ${anchors[i][0]} -> ${anchors[i][1]}`);
    }
  }
  return options.beyondScale === undefined ? { level, anchors } : { level, anchors, beyondScale: options.beyondScale };
}

/**
 * A ladder's pairs on a level: `adult` is the list of adult edges, from
 * `firstFt` on, whose gaps alternate through `adultSteps` (one step: an even
 * ladder; two: band, gap, band, gap...). The level's ladder starts at
 * levelDistanceFt(first) and steps the divided spacing (levelSpacingFt).
 */
export function levelLadder(level: PlayLevel, firstFt: number, adultSteps: readonly number[], count: number): Array<[number, number]> {
  const pairs: Array<[number, number]> = [];
  let adult = firstFt;
  let short = levelDistanceFt(firstFt, level);
  for (let i = 0; i < count; i += 1) {
    pairs.push([adult, short]);
    const step = adultSteps[i % adultSteps.length];
    adult += step;
    short += levelSpacingFt(step, level);
  }
  return pairs;
}

function segmentOf(anchors: LevelDistanceMap['anchors'], value: number, column: 0 | 1): number {
  // The segment holding `value`; past the last anchor, the last segment.
  for (let i = 1; i < anchors.length; i += 1) {
    if (value <= anchors[i][column]) return i;
  }
  return anchors.length - 1;
}

/** Past the last anchor the map keeps `beyondScale`, or the plain divisor; behind the ball, the first segment's slope. */
function project(map: LevelDistanceMap, value: number, from: 0 | 1): number {
  const { anchors } = map;
  const to = from === 0 ? 1 : 0;
  if (!Number.isFinite(value)) return value;
  const last = anchors[anchors.length - 1];
  const scale = map.beyondScale ?? levelToleranceScale(map.level);
  const outside = from === 0 ? scale : 1 / scale;
  if (value <= 0) return value * (anchors.length > 1 ? (anchors[1][to] / anchors[1][from]) : outside);
  if (value >= last[from]) return last[to] + (value - last[from]) * outside;
  const i = segmentOf(anchors, value, from);
  const a = anchors[i - 1];
  const b = anchors[i];
  return a[to] + ((value - a[from]) / (b[from] - a[from])) * (b[to] - a[to]);
}

/** An adult (engine) distance down the line, where the level sees and putts it. */
export function levelFtFor(map: LevelDistanceMap, adultFt: number): number {
  return project(map, adultFt, 0);
}

/** A distance measured on the level, in the engine's adult feet. */
export function adultFtFor(map: LevelDistanceMap, levelFt: number): number {
  return project(map, levelFt, 1);
}

/**
 * How one seat's turn sees the field. Tour seats see the engine's own numbers
 * (ADULT_DISTANCES is the identity); a Rookie or Pro seat sees its ladder.
 * Every host converts through one of these, so seats on different levels can
 * share a gate, a board and a score.
 */
export interface DistanceView {
  readonly level: PlayLevel;
  /** An engine distance down the line, as drawn on this turn (continuous). */
  ft(adultFt: number): number;
  /** An engine target (a cup, a kick), as putted on this turn: whole feet off Tour. */
  targetFt(adultFt: number): number;
  /** A distance measured on this turn, in engine feet. */
  engineFt(measuredFt: number): number;
  /** An engine ring, radius or leave, as drawn on this turn. */
  tolFt(adultFt: number): number;
  /** A leave measured on this turn, in engine feet. */
  engineTolFt(measuredFt: number): number;
}

/** The Tour view: the engine's own field. */
export const ADULT_DISTANCES: DistanceView = {
  level: 'tour',
  ft: (value) => value,
  targetFt: (value) => value,
  engineFt: (value) => value,
  tolFt: (value) => value,
  engineTolFt: (value) => value,
};

/** True on a Rookie or Pro turn: the field is drawn shorter. */
export function isShortField(view: DistanceView): boolean {
  return view.level !== 'tour';
}

export function levelDistanceView(map: LevelDistanceMap): DistanceView {
  const scale = levelToleranceScale(map.level);
  return {
    level: map.level,
    ft: (value) => levelFtFor(map, value),
    targetFt: (value) => Math.max(LEVEL_MIN_DISTANCE_FT, Math.round(levelFtFor(map, value))),
    engineFt: (value) => adultFtFor(map, value),
    tolFt: (value) => value * scale,
    engineTolFt: (value) => value / scale,
  };
}

/** A mode's maps, one per shortened level (kidModes.ts). */
export type LevelMaps = Readonly<Record<ShortLevel, LevelDistanceMap>>;

/** One view per seat: Rookie and Pro seats see their map, Tour seats the adult field. */
export function seatDistanceViews(maps: LevelMaps, levels: readonly PlayLevel[]): DistanceView[] {
  const views: Record<PlayLevel, DistanceView> = {
    rookie: levelDistanceView(maps.rookie),
    pro: levelDistanceView(maps.pro),
    tour: ADULT_DISTANCES,
  };
  return levels.map((level) => views[level]);
}

/**
 * The levels a match plays with: never online (both phones must fold the
 * same putts, so every seat is Tour), and against the computer only the
 * person's seat (the computer always plays Tour).
 */
export function matchSeatLevels(levels: readonly PlayLevel[], options: { live: boolean; computerSeats?: readonly number[]; count: number }): PlayLevel[] {
  return Array.from({ length: options.count }, (_, seat) =>
    !options.live && !(options.computerSeats ?? []).includes(seat) ? levels[seat] ?? 'tour' : 'tour'
  );
}

/** "Sam" -> "Sam · ROOKIE" / "Sam · PRO" for surfaces that only carry a name (the TV's scoreboards); Tour adds nothing. */
export function seatLevelLabel(name: string, level: PlayLevel): string {
  return level === 'tour' ? name : `${name} · ${PLAY_LEVEL_LABEL[level]}`;
}

/** The setup note under the players: who plays a shorter level, or null when everyone plays Tour. */
export function seatLevelsNote(names: readonly string[], levels: readonly PlayLevel[]): string | null {
  const sentences: string[] = [];
  for (const level of SHORT_LEVELS) {
    const who = names.filter((_, index) => levels[index] === level);
    if (who.length === 0) continue;
    const all = who.length === names.length && names.length > 1;
    const subject = all ? 'All players' : who.join(', ');
    const verb = who.length === 1 && !all ? 'plays' : 'play';
    sentences.push(`${subject} ${verb} ${PLAY_LEVEL_NAME[level]}: ${PLAY_LEVEL_DISTANCE[level]}.`);
  }
  return sentences.length > 0 ? sentences.join(' ') : null;
}

// ---------------------------------------------------------------------------
// The scene on a seat's turn. Hosts build the field in engine (adult) feet and
// these move it to where that seat putts: a cup to its whole-foot target on
// the level, its ring divided, a standing distance marker to the level's
// distance with its number relabeled. The Tour view returns the very same
// objects.
// ---------------------------------------------------------------------------

/** How far out a shortened field's standing markers stand (ft). */
export const SHORT_MARKER_RAIL_FT = 3;

/** The cups (and rings) as this seat sees them. */
export function cupsForView<T extends Pick<PuttSceneExtraCup, 'distanceFt' | 'ringRadiusFt'>>(cups: readonly T[], view: DistanceView): T[] {
  if (!isShortField(view)) return cups as T[];
  return cups.map((cup) => ({
    ...cup,
    distanceFt: view.targetFt(cup.distanceFt),
    ...(cup.ringRadiusFt !== undefined ? { ringRadiusFt: view.tolFt(cup.ringRadiusFt) } : {}),
  }));
}

/** Standing markers and bands as this seat sees them: a label that was the adult distance becomes the level's. */
export function bandsForView(bands: readonly PuttSceneRolloutBand[], view: DistanceView): PuttSceneRolloutBand[] {
  if (!isShortField(view)) return bands as PuttSceneRolloutBand[];
  return bands.map((band) => {
    const point = band.minFt === band.maxFt;
    const minFt = point ? view.targetFt(band.minFt) : view.ft(band.minFt);
    const maxFt = point ? minFt : view.ft(band.maxFt);
    // A standing marker (Football's, Baseball's) stands 8 ft out at 2.5x on
    // the adult field, off the screen beside a 5 ft Rookie or Pro cup: on a
    // shortened field it stands at half the size, 3 ft out, so the near ones
    // stay in view.
    const marker = band.labelOnly && band.labelRailFt == null && band.labelOrientation !== 'flat'
      ? { labelScale: Math.max(1, (band.labelScale ?? 1) / 2), labelRailFt: SHORT_MARKER_RAIL_FT }
      : {};
    return {
      ...band,
      minFt,
      maxFt,
      ...marker,
      ...(band.label === String(band.minFt) ? { label: String(minFt) } : {}),
    };
  });
}

/**
 * A ball one seat putted (`fromView`), drawn on another seat's field: a
 * batter's or the offense's ball left on the green while the other side
 * answers. Same start line, its distance moved through the engine's feet.
 */
export function ballForView(
  measuredFt: number,
  angleDeg: number | null,
  fromView: DistanceView,
  toView: DistanceView,
): { xFt: number; zFt: number } {
  const distanceFt = fromView === toView ? measuredFt : toView.ft(fromView.engineFt(measuredFt));
  const xFt = distanceFt * Math.tan(((angleDeg ?? 0) * Math.PI) / 180);
  return { xFt, zFt: -distanceFt };
}
